import { kmApart } from './map';

/** How far up or down a height must go before it counts: as the server's track reading. */
const WOBBLE = 3;

/** The most points the ground's heights are asked for at once. */
export const MOST_HEIGHTS = 2000;

/** What a stretch of way adds up to: its length in metres and its climb. */
export interface Stretch {
  metres: number;
  ascent: number;
  descent: number;
  highest: number | null;
  lowest: number | null;
}

/** How long a path is, in metres, along the great circle. */
export function lengthOf(path: readonly [number, number][]): number {
  let km = 0;
  for (let at = 1; at < path.length; at++) {
    const [a, b] = [path[at - 1], path[at]] as [[number, number], [number, number]];
    km += kmApart({ lon: a[0], lat: a[1] }, { lon: b[0], lat: b[1] });
  }
  return km * 1000;
}

/** A path's length and climb, as felt: a change counts once it passes the GPS's wobble. */
export function stretchOf(
  path: readonly [number, number][],
  heights: readonly (number | null | undefined)[],
): Stretch {
  const said = heights.filter((height) => typeof height === 'number');
  let [ascent, descent] = [0, 0];
  let level = said[0];
  for (const height of said) {
    if (level !== undefined && Math.abs(height - level) >= WOBBLE) {
      if (height > level) ascent += height - level;
      else descent += level - height;
      level = height;
    }
  }
  return {
    metres: lengthOf(path),
    ascent: Math.round(ascent),
    descent: Math.round(descent),
    highest: said.length === 0 ? null : Math.max(...said),
    lowest: said.length === 0 ? null : Math.min(...said),
  };
}

/** How long a walk takes, Naismith's way: 5 km an hour and an hour more for each 600 m up. */
export function walkMinutes(metres: number, ascent: number): number {
  return Math.max(1, Math.round((metres / 5000) * 60 + (ascent / 600) * 60));
}

/** Every `n`th point of a path, its last always kept, so at most `most` are left. */
export function thinned<T>(path: readonly T[], most = MOST_HEIGHTS): T[] {
  const step = Math.max(1, Math.ceil(path.length / most));
  const kept = path.filter((_, at) => at % step === 0);
  const last = path.at(-1);
  if (last !== undefined && kept.at(-1) !== last) kept.push(last);
  return kept;
}

/** The segments of a drawn way joined into one path, each joint once. */
export function joined(segments: readonly (readonly [number, number][])[]): [number, number][] {
  const path: [number, number][] = [];
  for (const segment of segments) {
    for (const point of segment) {
      const last = path.at(-1);
      if (last?.[0] !== point[0] || last[1] !== point[1]) path.push(point);
    }
  }
  return path;
}
